/**
 * 근태 시드 — 휴가(vacation_request), 근무 신청(user_schedule), 휴일(holiday), 연차 부여
 * (vacation_grant). Notion 스키마 모양 그대로다.
 *
 * 휴가 유형은 백엔드가 깔아 두는 넷(연차 · 반차 · 반반차 · 기타)에 사무소가 더한 둘(예비군 · 경조사)이다.
 * 반차 · 반반차는 그 하루의 시각 범위로 남는다(09시~13시처럼). 근무 신청은 근무 유형이 승인을 요구하지
 * 않으면 넣는 즉시 확정이다. 휴일은 하루에 한 행이므로 연휴는 날짜마다 한 행씩 깔린다.
 */
import type {
  ScheduleCategory,
  UserSchedule,
  Vacation,
  VacationGrant,
  VacationKind,
} from "@/types";
import { holidayDates, nextDay } from "./holidays";
import { at } from "./seed";
import { staff } from "./staff";

const STANDARD_HOURS = 8;

const nameOf = (userId: string) => staff.find((u) => u.id === userId)?.name ?? userId;

/* ── 휴가 유형 ─────────────────────────────────────────────────────────── */

const kind = (
  id: string,
  name: string,
  color: string,
  unit_days: number | null,
  rules: Partial<VacationKind>,
  sort_order: number
): VacationKind => ({
  id,
  name,
  color,
  unit_days,
  is_annual: false,
  deducts_annual: false,
  reason_required: false,
  requires_approval: true,
  attachment_before: false,
  attachment_after: false,
  attachment_after_days: null,
  ...rules,
  sort_order,
});

/** 앞의 넷은 백엔드 마이그레이션이 깔아 두는 것과 같은 id · 이름 · 색이다. */
export const vacationKinds: VacationKind[] = [
  kind(
    "5e2b0001-0000-4000-8000-000000000001",
    "연차",
    "#3182f6",
    1,
    { deducts_annual: true, is_annual: true },
    10
  ),
  kind(
    "5e2b0001-0000-4000-8000-000000000002",
    "반차",
    "#3182f6",
    0.5,
    { deducts_annual: true },
    20
  ),
  kind(
    "5e2b0001-0000-4000-8000-000000000003",
    "반반차",
    "#3182f6",
    0.25,
    { deducts_annual: true },
    30
  ),
  kind(
    "5e2b0001-0000-4000-8000-000000000004",
    "기타",
    "#8b95a1",
    null,
    { reason_required: true },
    40
  ),
  kind(
    "vk-reserve",
    "예비군",
    "#a234c7",
    null,
    { attachment_after: true, attachment_after_days: 7 },
    50
  ),
  kind("vk-family", "경조사", "#fe9800", null, { attachment_before: true }, 60),
];

/** 시드의 휴가 이름(vk-…) → 유형 */
const KIND_OF: Record<string, VacationKind> = {
  "vk-annual": vacationKinds[0],
  "vk-half": vacationKinds[1],
  "vk-quarter": vacationKinds[2],
  "vk-other": vacationKinds[3],
  "vk-reserve": vacationKinds[4],
  "vk-family": vacationKinds[5],
};

/* ── 휴가 ──────────────────────────────────────────────────────────────── */

type Decision =
  { kind: "auto"; on: string } | { kind: "approved"; by: string; on: string; note?: string } | null;

interface VacationSeed {
  id: string;
  user: string;
  /** 시드의 휴가 이름(vk-…) — KIND_OF 로 유형이 된다 */
  type: string;
  start: string;
  end?: string;
  /** 하루보다 짧게 쓸 때만. 30분 단위 */
  time?: [string, string];
  reason: string;
  requested_on: string;
  decision: Decision;
  /** 승인된 휴가의 취소 신청 — 관리자 결정을 기다린다 */
  cancel_requested?: string;
  /** 신청할 때 낸 첨부 파일 이름(사전 첨부) */
  files?: string[];
}

const auto = (on: string): Decision => ({ kind: "auto", on });
const by = (who: string, on: string, note?: string): Decision => ({
  kind: "approved",
  by: who,
  on,
  note,
});

const VACATION_SEED: VacationSeed[] = [
  {
    id: "01",
    user: "u-pm",
    type: "vk-annual",
    start: "2026-09-15",
    reason: "개인 연차",
    requested_on: "2026-09-04",
    decision: auto("2026-09-04"),
  },
  {
    id: "03",
    user: "u-pm",
    type: "vk-annual",
    start: "2026-11-02",
    end: "2026-11-03",
    reason:
      "가족 여행 — 부모님 칠순을 맞아 제주에 다녀옵니다. 2일(월)은 오전에 출발해 급한 연락은 메신저로 받을 수 있고, 3일(화)은 종일 연락이 어렵습니다. 진행 중인 도면 검토는 31일까지 마쳐 두겠습니다.",
    requested_on: "2026-10-21",
    decision: null,
  },
  {
    id: "04",
    user: "u-pm2",
    type: "vk-annual",
    start: "2026-09-29",
    end: "2026-09-30",
    reason: "추석 연휴 연결",
    requested_on: "2026-09-10",
    decision: by("변경민", "2026-09-11"),
  },
  // 오후 반차 — 유형은 반차, 그 하루의 14시부터 18시까지다.
  {
    id: "06",
    user: "u-pm3",
    type: "vk-half",
    start: "2026-09-17",
    time: ["14:00", "18:00"],
    reason: "병원",
    requested_on: "2026-09-08",
    decision: auto("2026-09-08"),
  },
  {
    id: "07",
    user: "u-staff",
    type: "vk-half",
    start: "2026-09-08",
    time: ["09:00", "13:00"],
    reason: "병원",
    requested_on: "2026-08-31",
    decision: auto("2026-08-31"),
  },
  {
    id: "08",
    user: "u-staff",
    type: "vk-quarter",
    start: "2026-10-16",
    time: ["16:00", "18:00"],
    reason: "은행 업무",
    requested_on: "2026-10-07",
    decision: auto("2026-10-07"),
  },
  {
    id: "09",
    user: "u-staff",
    type: "vk-annual",
    start: "2026-10-29",
    end: "2026-10-30",
    reason: "가족 행사",
    requested_on: "2026-10-19",
    decision: null,
  },
  {
    id: "10",
    user: "u-st2",
    type: "vk-annual",
    start: "2026-09-21",
    end: "2026-09-23",
    reason: "추석 연휴 연결",
    requested_on: "2026-09-03",
    decision: by("u-owner", "2026-09-04"),
  },
  // 경조사 — 연차 잔여에서 빠지지 않는다. 사유가 반드시 있다.
  {
    id: "24",
    user: "u-st2",
    type: "vk-family",
    start: "2026-10-26",
    end: "2026-10-27",
    reason: "경조사",
    requested_on: "2026-10-20",
    decision: by("변경민", "2026-10-20"),
  },
  // 관리자 알림의 「휴가 신청: 김서연」 — 결재 팝업에서 두 건을 한 번에 본다.
  {
    id: "25",
    user: "u-st2",
    type: "vk-family",
    start: "2026-11-10",
    end: "2026-11-12",
    reason: "형제 결혼",
    requested_on: "2026-11-04",
    decision: null,
    files: ["청첩장.pdf"],
  },
  {
    id: "26",
    user: "u-st2",
    type: "vk-annual",
    start: "2026-11-20",
    reason: "개인 연차",
    requested_on: "2026-11-04",
    decision: null,
  },
  {
    id: "12",
    user: "u-st3",
    type: "vk-annual",
    start: "2026-09-11",
    reason: "이사",
    requested_on: "2026-09-01",
    decision: auto("2026-09-01"),
  },
  {
    id: "13",
    user: "u-st3",
    type: "vk-annual",
    start: "2026-10-23",
    reason: "개인 연차",
    requested_on: "2026-10-19",
    decision: null,
  },
  {
    id: "14",
    user: "u-st4",
    type: "vk-annual",
    start: "2026-09-18",
    reason: "개인 연차",
    requested_on: "2026-09-07",
    decision: null,
  },
  {
    id: "16",
    user: "u-st4",
    type: "vk-half",
    start: "2026-10-13",
    time: ["14:00", "18:00"],
    reason: "병원",
    requested_on: "2026-10-05",
    decision: auto("2026-10-05"),
  },
  {
    id: "17",
    user: "u-st5",
    type: "vk-annual",
    start: "2026-10-02",
    reason: "학교 행사",
    requested_on: "2026-09-22",
    decision: auto("2026-09-22"),
  },
  {
    id: "18",
    user: "u-st6",
    type: "vk-annual",
    start: "2026-09-14",
    reason: "개인 연차",
    requested_on: "2026-09-02",
    decision: auto("2026-09-02"),
  },
  {
    id: "19",
    user: "u-acc",
    type: "vk-half",
    start: "2026-09-04",
    time: ["09:00", "13:00"],
    reason: "병원",
    requested_on: "2026-08-26",
    decision: auto("2026-08-26"),
  },
  // 승인됐지만 취소 신청을 낸 건 — 관리자 결정을 기다린다.
  {
    id: "20",
    user: "u-acc",
    type: "vk-annual",
    start: "2026-10-08",
    reason: "개인 연차",
    requested_on: "2026-09-28",
    decision: auto("2026-09-28"),
    cancel_requested: "2026-10-05",
  },
  {
    id: "21",
    user: "변경민",
    type: "vk-annual",
    start: "2026-09-22",
    end: "2026-09-23",
    reason: "추석 연휴 연결",
    requested_on: "2026-09-07",
    decision: by("u-owner", "2026-09-07"),
  },
  // 예비군 — 연차를 깎지 않는 휴가 유형이다(예전에는 기타로 넣었다).
  {
    id: "22",
    user: "변경민",
    type: "vk-reserve",
    start: "2026-10-05",
    reason: "예비군 훈련",
    requested_on: "2026-09-25",
    decision: by("u-owner", "2026-09-25"),
  },
];

const isWeekendDay = (date: string) => [0, 6].includes(new Date(`${date}T00:00:00Z`).getUTCDay());
const isWorkday = (date: string) => !isWeekendDay(date) && !holidayDates.has(date);

/** 종일은 근무일 수, 부분 휴가는 시간 ÷ 하루 기준 시간. 점심은 빼지 않는다. */
function daysOf(seed: VacationSeed): number {
  let count = 0;
  for (let day = seed.start; day <= (seed.end ?? seed.start); day = nextDay(day))
    if (isWorkday(day)) count++;
  if (!seed.time) return count;
  if (!count) return 0;
  const [start, end] = seed.time;
  const minutes =
    Number(end.slice(0, 2)) * 60 +
    Number(end.slice(3)) -
    (Number(start.slice(0, 2)) * 60 + Number(start.slice(3)));
  return Math.round((minutes / 60 / STANDARD_HOURS) * 100) / 100;
}

export const vacations: Vacation[] = VACATION_SEED.map((seed) => {
  const decision = seed.decision;
  const approved = decision !== null;
  return {
    id: `vacation-${seed.id}`,
    user_id: seed.user,
    user_name: nameOf(seed.user),
    kind_id: KIND_OF[seed.type].id,
    kind_name: KIND_OF[seed.type].name,
    kind_color: KIND_OF[seed.type].color,
    deducts_annual: KIND_OF[seed.type].deducts_annual,
    attachments: (seed.files ?? []).map((name, index) => ({
      id: `vacation-${seed.id}-file-${index}`,
      stage: "before" as const,
      file_name: name,
      content_type: "application/pdf",
      byte_size: 184_320,
      url: `/api/v1/attendance-attachments/vacation-${seed.id}-file-${index}/content/`,
    })),
    attachment_due: false,
    start_date: seed.start,
    end_date: seed.end ?? seed.start,
    start_time: seed.time ? `${seed.time[0]}:00` : null,
    end_time: seed.time ? `${seed.time[1]}:00` : null,
    days: daysOf(seed),
    reason: seed.reason,
    status: approved ? "approved" : "pending",
    auto_approved: decision?.kind === "auto",
    approver_id: decision?.kind === "approved" ? decision.by : null,
    decided_at: decision ? at(decision.on, "11:30") : null,
    decision_note: decision?.kind === "approved" ? (decision.note ?? "") : "",
    cancel_requested_at: seed.cancel_requested ? at(seed.cancel_requested, "09:40") : null,
    created_at: at(seed.requested_on, "10:12"),
  };
});

/* ── 근무 신청 ─────────────────────────────────────────────────────────── */

interface ScheduleSeed {
  id: string;
  user: string;
  kind: "field" | "trip" | "other";
  project?: string;
  start: string;
  end?: string;
  times?: [string, string];
  reason: string;
  created_on: string;
}

const SCHEDULE_SEED: ScheduleSeed[] = [
  {
    id: "02",
    user: "u-pm",
    kind: "field",
    project: "p-2506",
    start: "2026-10-06",
    times: ["13:00", "18:00"],
    reason: "제주 한남숲길 현장 미팅",
    created_on: "2026-10-02",
  },
  {
    id: "05",
    user: "u-pm2",
    kind: "field",
    project: "p-2604",
    start: "2026-10-14",
    times: ["10:00", "15:00"],
    reason: "래미안 견본주택 발주처 미팅",
    created_on: "2026-10-13",
  },
  {
    id: "11",
    user: "u-st2",
    kind: "trip",
    project: "p-2410",
    start: "2026-10-07",
    end: "2026-10-08",
    reason: "세종로공원 서울시 협의 출장",
    created_on: "2026-10-05",
  },
  {
    id: "15",
    user: "u-st4",
    kind: "field",
    project: "p-2503",
    start: "2026-10-01",
    times: ["09:30", "13:00"],
    reason: "삼표 트레이닝 센터 외장재 샘플 확인",
    created_on: "2026-09-29",
  },
  {
    id: "25",
    user: "u-st3",
    kind: "field",
    project: "p-2506",
    start: "2026-10-29",
    times: ["13:00", "17:00"],
    reason: "제주 한남숲길 사이니지 목업 확인",
    created_on: "2026-10-27",
  },
  {
    id: "26",
    user: "u-pm",
    kind: "trip",
    project: "p-2603",
    start: "2026-11-04",
    end: "2026-11-05",
    reason: "신흥 MST 공장 원주시청 협의",
    created_on: "2026-10-28",
  },
  {
    id: "23",
    user: "u-owner",
    kind: "trip",
    project: "p-2603",
    start: "2026-10-19",
    end: "2026-10-20",
    reason: "신흥 MST 공장 답사",
    created_on: "2026-10-15",
  },
];

/**
 * 백엔드 마이그레이션이 깔아 두는 셋과 같은 id · 이름 · 색이다. 신청 폼에서는 휴가 유형 다음에 온다.
 * 데모가 근무 신청 결재를 보여 주도록 출장만 승인을 요구한다(백엔드의 기본은 셋 다 승인 없음).
 */
const category = (
  id: string,
  name: string,
  color: string,
  sort_order: number,
  rules: Partial<ScheduleCategory> = {}
): ScheduleCategory => ({
  id,
  name,
  color,
  requires_approval: false,
  attachment_before: false,
  attachment_after: false,
  attachment_after_days: null,
  all_day: false,
  ...rules,
  sort_order,
});

export const scheduleCategories: ScheduleCategory[] = [
  category("3f1a0001-0000-4000-8000-000000000001", "외근", "#18a5a5", 10),
  category("3f1a0001-0000-4000-8000-000000000002", "출장", "#03b26c", 20, {
    requires_approval: true,
    attachment_after: true,
    attachment_after_days: 7,
    all_day: true,
  }),
  category("3f1a0001-0000-4000-8000-000000000003", "기타 근무", "#fe9800", 30),
];

const CATEGORY_OF = { field: 0, trip: 1, other: 2 } as const;

export const schedules: UserSchedule[] = SCHEDULE_SEED.map((seed) => ({
  id: `schedule-${seed.id}`,
  user_id: seed.user,
  user_name: nameOf(seed.user),
  category_id: scheduleCategories[CATEGORY_OF[seed.kind]].id,
  category_name: scheduleCategories[CATEGORY_OF[seed.kind]].name,
  category_color: scheduleCategories[CATEGORY_OF[seed.kind]].color,
  starts_at: at(seed.start, seed.times?.[0] ?? "09:00"),
  ends_at: at(seed.end ?? seed.start, seed.times?.[1] ?? "18:00"),
  // 시각을 적지 않은 시드는 종일 유형(출장)으로 낸 것으로 본다.
  all_day: seed.kind === "trip" && !seed.times,
  project_id: seed.project ?? null,
  reason: seed.reason,
  // 시드의 근무 신청은 모두 확정된 것이다 — 승인을 요구하는 출장도 이미 승인을 받았다.
  status: "approved",
  approver_id: null,
  decided_at: null,
  decision_note: "",
  cancelled_at: null,
  created_at: at(seed.created_on, "10:12"),
  attachments: [],
  attachment_due: false,
}));

/* ── 연차 부여 ─────────────────────────────────────────────────────────── */

/** 올해 부여한 연차와, 시드 창(9월 1일) 전에 이미 쓴 연차(이전 사용으로 들여온다). */
const GRANT_SEED: Record<string, { granted: number; used_before: number }> = {
  "u-owner": { granted: 25, used_before: 6 },
  변경민: { granted: 17, used_before: 4 },
  "u-pm": { granted: 20, used_before: 8 },
  "u-pm2": { granted: 19, used_before: 5 },
  "u-pm3": { granted: 18, used_before: 2.5 },
  "u-staff": { granted: 15, used_before: 3 },
  "u-st2": { granted: 16, used_before: 3.5 },
  "u-st3": { granted: 11, used_before: 3 },
  "u-st4": { granted: 15, used_before: 9 },
  "u-st5": { granted: 3, used_before: 0 },
  "u-st6": { granted: 15, used_before: 12 },
  "u-acc": { granted: 17, used_before: 6 },
};

export const GRANT_PERIOD = { from: "2026-01-01", to: "2027-01-01" };

/** 부여 기간 한 건씩. 사용과 잔여는 handler 가 휴가에서 센다 — 컬럼으로 두지 않는다. */
export const vacationGrants: Omit<VacationGrant, "used" | "expired" | "remaining">[] =
  Object.entries(GRANT_SEED).map(([userId, seed]) => ({
    id: `grant-${userId}`,
    user_id: userId,
    user_name: nameOf(userId),
    period_from: GRANT_PERIOD.from,
    period_to: GRANT_PERIOD.to,
    granted: seed.granted,
    adjusted: 0,
    prior_used: seed.used_before,
    note: "",
  }));

/**
 * 지난해 몫 — 연차 내역 표에 만료된 지급분이 보이게. 시스템 도입 전이라 쓴 날은 모두 이전 사용이고,
 * 쓰지 않은 2일은 만료일에 소멸한다(서버가 센다) — 잔여는 0 이라 지금 남은 연차에 섞이지 않는다.
 */
vacationGrants.push(
  ...Object.entries(GRANT_SEED).map(([userId, seed]) => ({
    id: `grant-${userId}-2025`,
    user_id: userId,
    user_name: nameOf(userId),
    period_from: "2025-01-01",
    period_to: "2026-01-01",
    granted: seed.granted - 1,
    adjusted: 0,
    prior_used: Math.max(0, seed.granted - 3),
    note: "",
  }))
);

/** 승인된 휴가로 그날 일하지 않은 시간 — Daily 시드가 그날의 한 일 시간을 줄이는 데 쓴다. */
export function leaveHoursOn(userId: string, date: string): number {
  const vacation = vacations.find(
    (row) =>
      row.user_id === userId &&
      row.status === "approved" &&
      row.start_date <= date &&
      date <= row.end_date
  );
  if (!vacation) return 0;
  if (!vacation.start_time || !vacation.end_time) return STANDARD_HOURS;
  return (
    (Number(vacation.end_time.slice(0, 2)) * 60 +
      Number(vacation.end_time.slice(3, 5)) -
      (Number(vacation.start_time.slice(0, 2)) * 60 + Number(vacation.start_time.slice(3, 5)))) /
    60
  );
}
